import { UserTokens, TokenStorage } from '@rhaimes/sdk'

const ACCESS_TOKEN_KEY = 'orul_auth_access_token'
const ID_TOKEN_KEY = 'orul_auth_id_token'
const REFRESH_TOKEN_KEY = 'orul_auth_refresh_token'
const EXPIRES_AT_KEY = 'orul_auth_expires_at'

/**
 * Custom TokenStorage que persiste todos os tokens no localStorage
 *
 * O DefaultTokenStorage do Orul Auth SDK só persiste o refreshToken,
 * deixando accessToken, idToken e expiresAt apenas em memória.
 * Esta implementação persiste todos os tokens no localStorage para
 * manter a sessão após refresh da página.
 */
export class PersistentTokenStorage implements TokenStorage {
  // eslint-disable-next-line class-methods-use-this
  getTokens(): UserTokens | null {
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const accessToken = window.localStorage.getItem(ACCESS_TOKEN_KEY)
      const idToken = window.localStorage.getItem(ID_TOKEN_KEY)
      const refreshToken = window.localStorage.getItem(REFRESH_TOKEN_KEY)
      const expiresAtStr = window.localStorage.getItem(EXPIRES_AT_KEY)

      if (!accessToken || !idToken || !refreshToken || !expiresAtStr) {
        return null
      }

      const expiresAt = parseInt(expiresAtStr, 10)
      if (Number.isNaN(expiresAt)) {
        return null
      }

      return {
        accessToken,
        idToken,
        refreshToken,
        expiresAt,
      }
    } catch {
      return null
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async setTokens(tokens: UserTokens): Promise<void> {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken)
      window.localStorage.setItem(ID_TOKEN_KEY, tokens.idToken)
      window.localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken)
      window.localStorage.setItem(EXPIRES_AT_KEY, tokens.expiresAt.toString())
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async clearTokens(): Promise<void> {
    if (typeof window === 'undefined') {
      return
    }

    try {
      window.localStorage.removeItem(ACCESS_TOKEN_KEY)
      window.localStorage.removeItem(ID_TOKEN_KEY)
      window.localStorage.removeItem(REFRESH_TOKEN_KEY)
      window.localStorage.removeItem(EXPIRES_AT_KEY)
    } catch {
      // Silently fail
    }
  }

  isAuthenticated(): boolean {
    const tokens = this.getTokens()
    if (!tokens) {
      return false
    }

    return tokens.expiresAt > Date.now()
  }
}
